import React, { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  StyledDescription,
  StyledDescriptionText,
  StyledLabel,
  StyledLabelColumn,
  StyledLabelText,
  StyledSwitchGroup,
  StyledSwitch,
  StyledBackground,
  StyledToggle,
  StyledStatusLabel,
  StyledSwitchColumn,
  StyledHelperText,
  StyledHelperTextSpan,
} from './style';

type SwitchProps = {
  name: string;
  label: string;
  description?: string;
  helperText?: string;
};

type SwitchToggleProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  helperText?: string;
};

const SwitchToggle: FC<SwitchToggleProps> = ({ checked, onChange, disabled, helperText }) => {
  const toggle = (
    <StyledSwitchGroup>
      <StyledSwitch
        role="switch"
        aria-checked={checked}
        $disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
      >
        <StyledBackground $checked={checked} />
        <StyledToggle $checked={checked} />
      </StyledSwitch>
      <div>
        <StyledStatusLabel>{checked ? 'Access enabled' : 'Access disabled'}</StyledStatusLabel>
      </div>
    </StyledSwitchGroup>
  );

  if (!helperText) {
    return toggle;
  }

  return (
    <StyledSwitchColumn>
      {toggle}
      <StyledHelperText>
        <StyledHelperTextSpan>{helperText}</StyledHelperTextSpan>
      </StyledHelperText>
    </StyledSwitchColumn>
  );
};

const Switch: FC<SwitchProps> = ({ name, label, description, helperText }) => {
  const {
    control,
    formState: { isSubmitting, errors },
  } = useFormContext();

  const errorMessage = errors[name]?.message as string;
  const useDetailedLayout = Boolean(description || helperText);

  const switchControl = (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <SwitchToggle
          checked={!!field.value}
          onChange={field.onChange}
          disabled={isSubmitting}
          helperText={helperText}
        />
      )}
    />
  );

  return (
    <React.Fragment>
      <div className='box marginTop'>
        {useDetailedLayout ? (
          <>
            <div className='flex-1'>
              <StyledLabelColumn>
                <StyledLabel>
                  <StyledLabelText>{label}</StyledLabelText>
                </StyledLabel>
                {description && (
                  <StyledDescription>
                    <StyledDescriptionText>{description}</StyledDescriptionText>
                  </StyledDescription>
                )}
              </StyledLabelColumn>
            </div>
            <div className='flex-2'>{switchControl}</div>
          </>
        ) : (
          <>
            <div className='flex-1'>{label}</div>
            <div className='flex-2'>{switchControl}</div>
          </>
        )}
      </div>
      <div className='box'>
        <div className='flex-1' />
        {errorMessage && <div className='flex-2 error'>* {errorMessage}</div>}
      </div>
    </React.Fragment>
  );
};

export default Switch;
